import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, EnvelopeSimple, Phone, MapPin, CircleNotch } from "@phosphor-icons/react";
import { createLead } from "@/lib/api";
import { Reveal } from "./motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SERVICES = ["Website Design", "Web Development", "Branding & Identity", "SEO Optimisation", "E-Commerce", "Not sure yet"];
const BUDGETS = ["Under $1,000", "$1,000 – $2,500", "$2,500 – $5,000", "$5,000+"];

const EMPTY = { name: "", email: "", company: "", service: "", budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await createLead(form);
      toast.success("Thanks! We've received your enquiry and will be in touch shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-transparent border-0 border-b border-ink/20 rounded-none px-0 h-12 text-ink placeholder:text-ink/30 focus-visible:ring-0 focus-visible:border-gold font-mono text-sm";

  return (
    <section id="contact" className="bg-white text-ink py-24 lg:py-36" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        {/* Left info */}
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Let&apos;s talk</span>
            <h2 className="font-display font-extrabold tracking-mega leading-[0.95] text-5xl sm:text-6xl mt-4">
              Start your<br />
              <span className="font-editorial italic font-normal text-gold">project.</span>
            </h2>
            <p className="text-ink/55 mt-6 max-w-sm leading-relaxed">
              Tell us a little about what you need and we&apos;ll get back to you within one business day.
            </p>

            <div className="mt-12 space-y-6">
              <a href="mailto:mmdigitalagency05@gmail.com" data-testid="contact-email-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center group-hover:border-gold group-hover:text-gold transition-colors">
                  <EnvelopeSimple size={18} />
                </span>
                <span className="font-mono text-sm text-ink/70 group-hover:text-ink transition-colors">mmdigitalagency05@gmail.com</span>
              </a>
              <a href="tel:+61433784209" data-testid="contact-phone-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center group-hover:border-gold group-hover:text-gold transition-colors">
                  <Phone size={18} />
                </span>
                <span className="font-mono text-sm text-ink/70 group-hover:text-ink transition-colors">+61 433 784 209</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center">
                  <MapPin size={18} />
                </span>
                <span className="font-mono text-sm text-ink/70">Melbourne, Australia</span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right form */}
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-7">
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Name *</Label>
                  <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Email *</Label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Company</Label>
                <Input data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
              </div>

              <div className="grid sm:grid-cols-2 gap-7">
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Service</Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="contact-service" className="bg-transparent border-0 border-b border-ink/20 rounded-none px-0 h-12 text-ink font-mono text-sm focus:ring-0 data-[placeholder]:text-ink/30">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-cream border-ink/15 text-ink">
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} className="font-mono text-sm focus:bg-gold focus:text-ink">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Budget</Label>
                  <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                    <SelectTrigger data-testid="contact-budget" className="bg-transparent border-0 border-b border-ink/20 rounded-none px-0 h-12 text-ink font-mono text-sm focus:ring-0 data-[placeholder]:text-ink/30">
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent className="bg-cream border-ink/15 text-ink">
                      {BUDGETS.map((b) => (
                        <SelectItem key={b} value={b} className="font-mono text-sm focus:bg-gold focus:text-ink">{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Project details *</Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us about your project, goals and timeline..." rows={4} className="bg-transparent border-0 border-b border-ink/20 rounded-none px-0 text-ink placeholder:text-ink/30 focus-visible:ring-0 focus-visible:border-gold font-mono text-sm resize-none" />
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.97 }}
                data-testid="contact-submit-btn"
                className="group inline-flex items-center gap-2 bg-gold text-ink font-mono text-[12px] uppercase tracking-[0.14em] px-8 py-4 rounded-full hover:bg-cream transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? (
                  <><CircleNotch size={16} className="animate-spin" /> Sending...</>
                ) : (
                  <>Send Enquiry <ArrowUpRight weight="bold" size={15} className="group-hover:rotate-45 transition-transform duration-300" /></>
                )}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
